import clsx from 'clsx';

export interface PersonaCardProps {
  id?: string;
  name?: string;
  role?: string;
  description?: string;
  avatarUrl?: string;
  personality?: string[];
  difficulty?: 'easy' | 'medium' | 'hard';
  isSelected?: boolean;
  onSelect?: (id: string) => void;
}

const DEFAULT_DIFFICULTY_CONFIG = {
  easy: { color: 'text-green-600', label: 'Supportive' },
  medium: { color: 'text-yellow-600', label: 'Neutral' },
  hard: { color: 'text-red-600', label: 'Challenging' },
};

export function PersonaCard({
  id = '',
  name = '',
  role = '',
  description = '',
  avatarUrl,
  personality = [],
  difficulty = 'medium',
  isSelected = false,
  onSelect,
}: PersonaCardProps) {
  // Safe access for difficulty config
  const difficultyConfig = DEFAULT_DIFFICULTY_CONFIG[difficulty] ?? DEFAULT_DIFFICULTY_CONFIG.medium;

  // Handle empty/undefined persona gracefully
  if (!id && !name) {
    return (
      <div className="card p-4 text-sm text-gray-400">
        Persona not available.
      </div>
    );
  }

  // Safe first letter extraction
  const firstLetter = name && name.length > 0 ? name[0].toUpperCase() : '?';

  // Safe personality array
  const safePersonality = Array.isArray(personality) ? personality : [];

  const handleClick = () => {
    if (onSelect && id) {
      onSelect(id);
    }
  };

  return (
    <div
      className={clsx(
        'card cursor-pointer transition-all',
        isSelected
          ? 'ring-2 ring-primary-500 shadow-lg'
          : 'hover:shadow-md'
      )}
      onClick={handleClick}
    >
      <div className="card-body flex gap-4">
        {/* Avatar */}
        <div className="flex-shrink-0">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={name || 'Persona'}
              className="w-16 h-16 rounded-full object-cover"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
              <span className="text-2xl font-bold text-white">
                {firstLetter}
              </span>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">{name || 'Unknown'}</h3>
              {role && <p className="text-sm text-primary-600">{role}</p>}
            </div>
            {isSelected && (
              <CheckIcon className="w-5 h-5 text-primary-500" />
            )}
          </div>

          {description && (
            <p className="text-sm text-gray-500 mt-2 line-clamp-2">
              {description}
            </p>
          )}

          {/* Personality traits */}
          {safePersonality.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-3">
              {safePersonality.slice(0, 3).map((trait, index) => (
                <span
                  key={trait || index}
                  className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
                >
                  {trait}
                </span>
              ))}
            </div>
          )}

          {/* Difficulty indicator */}
          <p className={clsx('text-xs mt-2', difficultyConfig.color)}>
            {difficultyConfig.label}
          </p>
        </div>
      </div>
    </div>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}

// Persona list with selection
export interface PersonaListProps {
  personas?: PersonaCardProps[];
  selectedId?: string;
  onSelect?: (id: string) => void;
}

export function PersonaList({ personas = [], selectedId, onSelect }: PersonaListProps) {
  const safePersonas = Array.isArray(personas) ? personas : [];

  if (safePersonas.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        No personas available.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {safePersonas.map((persona, index) => (
        <PersonaCard
          key={persona.id || index}
          {...persona}
          isSelected={persona.id === selectedId}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}
